/**
 * Awareness tab: a stacked, collapsible list of the enabled awareness panels
 * (space weather, launches, conjunctions, SatNOGS), DSN Now for the deep-space
 * networks and the Artemis panels for the artemis network, with the sun-outage
 * badge and the terrain-mask progress on top. Layers are enabled from the
 * Layers section of the strip.
 */
import { Activity, Rocket, Satellite, Sun, Target } from 'lucide-react';
import { useAwareness } from '../../awareness/store';
import { isDeepSpaceNetwork, useLoader } from '../../store';
import { ArtemisPanels } from './awareness/ArtemisPanels';
import { ConjunctionsPanel } from './awareness/ConjunctionsPanel';
import { DsnNowPanel } from './awareness/DsnNowPanel';
import { LaunchesPanel } from './awareness/LaunchesPanel';
import { SatNogsPanel } from './awareness/SatNogsPanel';
import { Section } from './awareness/Section';
import { SpaceWeatherPanel } from './awareness/SpaceWeatherPanel';
import { SunOutageCard, TerrainProgressLine } from './awareness/SunOutageCard';

export function AwarenessPanel() {
  const network = useLoader((s) => s.network);
  const spaceWeatherEnabled = useLoader((s) => s.spaceWeatherEnabled);
  const launchesEnabled = useLoader((s) => s.launchesEnabled);
  const conjunctionsEnabled = useLoader((s) => s.conjunctionsEnabled);
  const satnogsEnabled = useLoader((s) => s.satnogsEnabled);
  const auroraEnabled = useLoader((s) => s.auroraEnabled);
  const gpsJamEnabled = useLoader((s) => s.gpsJamEnabled);
  const sunOutage = useLoader((s) => s.sunOutage);
  const auroraError = useAwareness((s) => s.auroraError);
  const gpsJamCount = useAwareness((s) => s.gpsJam?.length ?? null);

  const deepSpace = isDeepSpaceNetwork(network);
  const artemis = network === 'artemis';
  const anyPanel = spaceWeatherEnabled || launchesEnabled || conjunctionsEnabled || satnogsEnabled || deepSpace || sunOutage;
  const globeOnly = [auroraEnabled && 'aurora oval', gpsJamEnabled && 'GPS interference'].filter((x): x is string => Boolean(x));

  return (
    <div className="flex flex-col">
      <SunOutageCard />
      <TerrainProgressLine />
      {spaceWeatherEnabled && (
        <Section title="Space weather" icon={Sun}>
          <SpaceWeatherPanel />
        </Section>
      )}
      {launchesEnabled && (
        <Section title="Upcoming launches" icon={Rocket}>
          <LaunchesPanel />
        </Section>
      )}
      {conjunctionsEnabled && (
        <Section title="Conjunction watch" icon={Target}>
          <ConjunctionsPanel />
        </Section>
      )}
      {satnogsEnabled && (
        <Section title="SatNOGS observations" icon={Activity}>
          <SatNogsPanel />
        </Section>
      )}
      {deepSpace && (
        <Section title="DSN Now" icon={Satellite}>
          <DsnNowPanel />
        </Section>
      )}
      {artemis && <ArtemisPanels />}
      {globeOnly.length > 0 && (
        <div className="px-3 py-2 text-[10px] text-ink-3">
          On the globe: {globeOnly.join(', ')}
          {gpsJamEnabled && gpsJamCount !== null ? ` (${gpsJamCount} interference cells)` : ''}
          {auroraEnabled && auroraError ? `. Aurora feed: ${auroraError}` : ''}
        </div>
      )}
      {!anyPanel && globeOnly.length === 0 && (
        <div className="p-4 text-xs leading-relaxed text-ink-3">
          No awareness layers are on. Enable space weather, upcoming launches, the conjunction watch, SatNOGS
          observations, the aurora oval or GPS interference from the Layers section of the strip. DSN Now and the
          Artemis panels appear with the artemis and mars networks.
        </div>
      )}
    </div>
  );
}
